import * as THREE from 'three';

/**
 * Render Settings Configuration Interface
 */
export interface RenderSettings {
    // Anti-aliasing
    antialias: boolean;
    dpr: number;

    // Post-processing effects
    bloomEnabled: boolean;
    bloomIntensity: number;
    bloomThreshold: number;

    ssaoEnabled: boolean;
    ssaoRadius: number;
    ssaoIntensity: number;

    outlineEnabled: boolean;
    outlineColor: string;

    vignetteEnabled: boolean;
    vignetteIntensity: number;

    toneMappingMode: 'aces' | 'reinhard' | 'linear';
    exposure: number;

    // Shadows
    shadowsEnabled: boolean;
    shadowMapSize: number;
    shadowType: 'basic' | 'pcf' | 'pcfsoft' | 'vsm';
}

/**
 * Default Render Settings
 */
export const defaultRenderSettings: RenderSettings = {
    antialias: true,
    dpr: 1.5,

    bloomEnabled: false,
    bloomIntensity: 0.5,
    bloomThreshold: 0.9,

    ssaoEnabled: false,
    ssaoRadius: 0.5,
    ssaoIntensity: 15,

    outlineEnabled: true,
    outlineColor: '#00aaff',

    vignetteEnabled: false,
    vignetteIntensity: 0.3,

    toneMappingMode: 'aces',
    exposure: 1.0,

    shadowsEnabled: true,
    shadowMapSize: 2048,
    shadowType: 'pcfsoft',
};

/**
 * Material Presets for CAD
 */
export const MaterialPresets = {
    // Metal materials
    steel: {
        color: '#8a8a8a',
        metalness: 0.9,
        roughness: 0.3,
    },
    aluminum: {
        color: '#d4d4d4',
        metalness: 0.8,
        roughness: 0.4,
    },
    copper: {
        color: '#b87333',
        metalness: 0.9,
        roughness: 0.25,
    },

    // Building materials
    concrete: {
        color: '#808080',
        metalness: 0.0,
        roughness: 0.9,
    },
    brick: {
        color: '#a04030',
        metalness: 0.0,
        roughness: 0.95,
    },
    glass: {
        color: '#ffffff',
        metalness: 0.0,
        roughness: 0.0,
        transparent: true,
        opacity: 0.3,
    },
    wood: {
        color: '#8B4513',
        metalness: 0.0,
        roughness: 0.8,
    },

    // Plastic materials
    plasticWhite: {
        color: '#f0f0f0',
        metalness: 0.0,
        roughness: 0.3,
    },
    plasticBlack: {
        color: '#202020',
        metalness: 0.0,
        roughness: 0.3,
    },
};

/**
 * Create material from preset
 */
export function createMaterialFromPreset(
    presetName: keyof typeof MaterialPresets
): THREE.MeshStandardMaterial {
    const preset = MaterialPresets[presetName];
    return new THREE.MeshStandardMaterial(preset as any);
}

/**
 * Render Quality Presets
 */
export const QualityPresets: Record<string, Partial<RenderSettings>> = {
    low: {
        antialias: false,
        dpr: 1,
        bloomEnabled: false,
        ssaoEnabled: false,
        outlineEnabled: false,
        shadowsEnabled: false,
        shadowMapSize: 512,
    },
    medium: {
        antialias: true,
        dpr: 1,
        bloomEnabled: false,
        ssaoEnabled: false,
        outlineEnabled: true,
        shadowsEnabled: true,
        shadowMapSize: 1024,
    },
    high: {
        antialias: true,
        dpr: 1.5,
        bloomEnabled: false,
        ssaoEnabled: true,
        ssaoIntensity: 10,
        outlineEnabled: true,
        shadowsEnabled: true,
        shadowMapSize: 2048,
    },
    ultra: {
        antialias: true,
        dpr: 2,
        bloomEnabled: true,
        bloomIntensity: 0.3,
        ssaoEnabled: true,
        ssaoIntensity: 15,
        outlineEnabled: true,
        vignetteEnabled: true,
        shadowsEnabled: true,
        shadowMapSize: 4096,
    },
};
